import { useDisclosure } from "@heroui/react";
import { InputField, ModalBuilder, ModalError } from "../../modalBuilder";
import useErrorHandling from "@/hooks/useErrorHandling";
import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { LockSystem } from "@/api/lockSystems/lockSystems";
import { toast } from "react-toastify";
import { loadHandbooks } from "@/utils/loadHandbooks";
import {
  getIdFromLocationHandbook,
  getIdFromLockSystemHandbook,
} from "@/utils/getIdFromHandbooks";
import { api } from "@/api";
import { convertFieldsToModalFields } from "@/app/pages/dashboard/service/locks";

interface EditLockForm {
  display_name: string;
  lock_system: number;
  location?: number;
  shared: boolean;
  meta: Record<string, unknown>;
}

interface EditCustomLockModalProps {
  disclosure: ReturnType<typeof useDisclosure>;
  lockId: number;
  defaults?: {
    display_name?: string;
    lock_system?: number;
    location?: number;
    shared?: boolean;
    meta?: Record<string, unknown>;
  };
}

const BaseLockFields: InputField[] = [
  {
    label: "Название замка",
    name: "display_name",
    placeholder: "Офис 1",
    type: "text",
    isRequired: true,
  },
  {
    label: "Система",
    name: "lock_system",
    placeholder: "",
    type: "lock-system",
    isRequired: true,
  },
  {
    label: "Локация",
    name: "location",
    placeholder: "",
    type: "location",
    isRequired: false,
  },
  {
    label: "Общий замок",
    name: "shared",
    placeholder: "1",
    type: "boolean",
    isRequired: true,
  },
];

export function EditCustomLockModal({
  disclosure,
  lockId,
  defaults,
}: EditCustomLockModalProps) {
  const { isOpen, onOpenChange } = disclosure;
  const [modalErrors, setModalErros] = useState<ModalError[]>([]);

  const [lockSystem, setLockSystem] = useState<LockSystem | null>(null);
  const [metaFields, setMetaFields] = useState<InputField[]>([]);

  const prevLockSystemIdRef = useRef<number | null>(null);

  const handleError = useErrorHandling();

  const fields = useMemo(() => {
    return [...BaseLockFields, ...metaFields];
  }, [metaFields]);

  // Загружаем данные замка при открытии модала
  useEffect(() => {
    if (isOpen && lockId) {
      api.locks
        .get(lockId)
        .then((response) => {
          const lockData = response.data;
          // Если есть defaults, используем их, иначе данные из API
          const initialLockSystem =
            defaults?.lock_system || lockData.lock_system;

          if (initialLockSystem) {
            prevLockSystemIdRef.current = initialLockSystem;
            // Загружаем данные системы замков
            api.lockSystems
              .get(initialLockSystem)
              .then((systemResponse) => {
                setLockSystem(systemResponse.data);
              })
              .catch((err) => {
                console.log(err);
                toast.error("Не удалось получить данные системы замков");
              });
          }
        })
        .catch((err) => {
          console.log(err);
          toast.error("Не удалось получить данные замка");
        });
    }
  }, [isOpen, lockId, defaults]);

  useEffect(() => {
    if (lockSystem) {
      api.locks
        .getFields()
        .then((fields) => {
          const metaFields = fields.data.find(
            (field) =>
              field.system.toLowerCase() === lockSystem.type.toLowerCase(),
          )?.fields;
          if (metaFields) {
            setMetaFields(
              convertFieldsToModalFields(metaFields).map((field) => ({
                ...field,
                name: `meta.${field.name}`,
              })),
            );
          } else {
            setMetaFields([]);
          }
        })
        .catch((err) => {
          console.log(err);
          toast.error("Не удалось получить поля замка");
          setMetaFields([]);
        });
    } else {
      setMetaFields([]);
    }
  }, [lockSystem]);

  const onChange = useCallback((data: any) => {
    if (data.lock_system && prevLockSystemIdRef.current !== data.lock_system) {
      prevLockSystemIdRef.current = data.lock_system;
      api.lockSystems
        .get(data.lock_system)
        .then((response) => {
          setLockSystem(response.data);
        })
        .catch((err) => {
          console.log(err);
          toast.error("Не удалось получить данные системы замков");
          setLockSystem(null);
          prevLockSystemIdRef.current = null;
        });
    }

    if (!data.lock_system && prevLockSystemIdRef.current !== null) {
      setLockSystem(null);
      prevLockSystemIdRef.current = null;
    }
  }, []);

  const onSubmit = (data: EditLockForm) => {
    return new Promise<boolean>(async (resolve, _reject) => {
      const lockSystem = await getIdFromLockSystemHandbook(
        String(data.lock_system),
      );
      const location = await getIdFromLocationHandbook(String(data.location));

      api.locks
        .update(lockId, {
          display_name: data.display_name,
          lock_system: Number(lockSystem),
          lock_service_id: data.lock_system,
          location_id: Number(location),
          shared: Boolean(data.shared),
          meta: data.meta,
        })
        .then((_data) => {
          loadHandbooks({ handbooks: ["locks"] });
          disclosure.onClose();
          resolve(true);
        })
        .catch((err) => {
          console.log(err);
          const { errorMessage, parsedErrors } = handleError(err);
          setModalErros(parsedErrors || []);
          toast.error(errorMessage);
          resolve(false);
        });
    });
  };

  const onDelete = () => {
    return new Promise<boolean>(async (resolve, _reject) => {
      api.locks.delete(lockId).then((_data) => {
        resolve(true);
      });
    });
  };

  return (
    <ModalBuilder
      title="Редактирование замка"
      modalErrors={modalErrors}
      fields={fields}
      isOpen={isOpen}
      isSensitiveData={false}
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
      onChange={onChange}
      onDelete={onDelete}
      defaultValues={defaults}
    />
  );
}
